import React, { useEffect, useRef } from 'react';
import { Button, Divider } from 'src/components/atoms';
import { useOutsideClick } from 'src/hooks';
import { useTheme } from 'styled-components';
import { SpinnerButton } from '../SpinnerButton';
import { Stepper } from '../Stepper/Stepper';
import {
  ActionBar,
  BottomContainer,
  ChildrenContainer,
  ChildrenWrapper,
  ContentContainer,
  LeftButtons,
  StepperContainer,
  StepperModalStyled,
  StyledCloseIcon,
} from './StepperModal.styles';

interface Props {
  setOpen: (open: boolean) => void;
  open: boolean;
  'data-testid'?: string;
  isContinueButtonDisabled?: boolean;
  steps: { label: string; component: React.ReactNode }[];
  activeStep: number;
  finalStepContinueButtonText?: string;
  finalStepContinueButtonVariant?: 'primary' | 'secondary' | 'error';
  onCloseModal?: () => void;
  onBackButtonClick?: () => void;
  onContinueButtonClick?: () => void;
  submitButtonState?: 'pending' | 'default';
  disableCloseOnOutsideClick?: boolean;
  removeChildrenPadding?: boolean;
}

export const StepperModal = ({
  open,
  setOpen,
  isContinueButtonDisabled,
  steps,
  activeStep,
  finalStepContinueButtonText = 'Continue',
  finalStepContinueButtonVariant = 'primary',
  onCloseModal,
  onBackButtonClick,
  onContinueButtonClick,
  submitButtonState: buttonState,
  disableCloseOnOutsideClick = false,
  removeChildrenPadding = false,
  ...props
}: Props) => {
  const theme = useTheme();
  const ref = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);

  const stepLabels = steps.map((step) => step.label);
  const isBackButtonDisabled = activeStep === 0;
  const continueButtonDisabled = isContinueButtonDisabled;
  const continueButtonText =
    activeStep === steps.length - 1 ? finalStepContinueButtonText : 'Continue';
  const continueButtonVariant =
    activeStep === steps.length - 1
      ? finalStepContinueButtonVariant
      : 'primary';

  const handleCloseModal = () => {
    setOpen(false);
    ref.current?.close?.();
  };

  const handleContinueButtonClick = () => {
    onContinueButtonClick?.();
    childrenRef.current?.scrollIntoView();
  };
  const handleBackButtonClick = () => {
    onBackButtonClick?.();
    childrenRef.current?.scrollIntoView();
  };

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const onClose = () => {
      setOpen(false);
      onCloseModal?.();
    };

    element.addEventListener('close', onClose);

    return () => {
      element.removeEventListener('close', onClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflowY = 'hidden';
      ref.current?.showModal?.();
    } else {
      document.body.style.overflowY = 'auto';
      ref.current?.close?.();
    }
  }, [open]);

  useOutsideClick(contentRef, handleCloseModal, disableCloseOnOutsideClick);

  return (
    <StepperModalStyled
      ref={ref}
      $open={open}
      data-testid={props['data-testid']}
    >
      <ContentContainer ref={contentRef}>
        <StepperContainer>
          <StyledCloseIcon
            className="uil uil-multiply"
            onClick={handleCloseModal}
            size="24px"
          />
          <Stepper activeStep={activeStep} steps={stepLabels} />
          <Divider
            variant="horizontal"
            color={theme.atlas.semantic.color.outline.disabled}
          />
        </StepperContainer>
        <ChildrenContainer data-testid={`${props['data-testid']}-body`}>
          <ChildrenWrapper
            ref={childrenRef}
            removePadding={removeChildrenPadding}
          >
            {steps[activeStep].component}
          </ChildrenWrapper>
        </ChildrenContainer>
        <BottomContainer>
          <Divider
            variant="horizontal"
            color={theme.atlas.semantic.color.outline.disabled}
          />
          <ActionBar>
            <LeftButtons>
              <Button
                variant="secondary"
                onClick={handleBackButtonClick}
                data-testid={`${props['data-testid']}-back`}
                width="140px"
                disabled={isBackButtonDisabled}
              >
                Back
              </Button>
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                data-testid={`${props['data-testid']}-cancel`}
                width="140px"
              >
                Cancel
              </Button>
            </LeftButtons>
            <SpinnerButton
              onClick={handleContinueButtonClick}
              data-testid={`${props['data-testid']}-confirm`}
              width="140px"
              disabled={continueButtonDisabled}
              loading={buttonState === 'pending'}
              variant={continueButtonVariant}
            >
              {continueButtonText}
            </SpinnerButton>
          </ActionBar>
        </BottomContainer>
      </ContentContainer>
    </StepperModalStyled>
  );
};
